//This feature is used to manage which workers have kiosk access for a jobsite.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import kioskManagementService from "./kioskManagementService";


const initialState = {
    jobsiteId: null,
    kioskAccess: [],
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: null,
}


// Get Kiosk Access List for a given jobsite.
export const getKioskAccessList = createAsyncThunk(
    'kioskManagement/workersWithAccess',
    async (jobsiteId, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await kioskManagementService.getKioskAccessList(jobsiteId, token);
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();

            if (error.response && error.response.data && error.response.data.error) {
                toast.info(error.response.data.error);
            }
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Grant kiosk access for a worker on a jobsite.
export const grantKioskAccess = createAsyncThunk(
    'kioskManagement/grantAccess',
    async ({ jobsiteId, workerId }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            const response = await kioskManagementService.grantKioskAccess(jobsiteId, workerId, token);
            toast.success(response.message || 'Kiosk access granted successfully');
            return { jobsiteId, workerId, worker: response.worker };
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            toast.error(message);
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Revoke kiosk access for a worker on a jobsite.
export const revokeKioskAccess = createAsyncThunk(
    'kioskManagement/revokeAccess',
    async ({ jobsiteId, workerId }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            const response = await kioskManagementService.revokeKioskAccess(jobsiteId, workerId, token);
            toast.success(response.message || 'Kiosk access revoked successfully');
            return { jobsiteId, workerId };
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            toast.error(message);
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const kioskManagementSlice = createSlice({
    name:'kioskManagement',
    initialState,
    reducers:{
        reset: (state) => initialState,
        
        setJobsiteId: (state, action) => {
            state.jobsiteId = action.payload;
        },
    },
    extraReducers: (builder) =>{
        builder 
        .addCase(getKioskAccessList.pending, (state) => {
            state.isLoading = true;
            state.isError = false;
            state.isSuccess = false;
            state.message = null;
        })
        .addCase(getKioskAccessList.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.isError = false;
            state.jobsiteId = action.meta.arg;
            state.kioskAccess = action.payload && Array.isArray(action.payload.workers)
                ? action.payload.workers
                : [];
        })
        .addCase(getKioskAccessList.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.isSuccess = false;
            state.message = action.payload;
            state.kioskAccess = [];
        })
        .addCase(grantKioskAccess.pending, (state) => {
            state.isLoading = true;
            state.isError = false;
            state.isSuccess = false;
            state.message = null;
        })
        .addCase(grantKioskAccess.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.isError = false;
            const grantedWorker = action.payload.worker;
            if (grantedWorker) {
                // Avoid duplicates by workerId
                const workerId = grantedWorker.id || grantedWorker.workerId;
                if (!state.kioskAccess.some((w) => (w.id || w.workerId) === workerId)) {
                    state.kioskAccess.push(grantedWorker);
                }
            }
        })
        .addCase(grantKioskAccess.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.isSuccess = false;
            state.message = action.payload;
        })
        .addCase(revokeKioskAccess.pending, (state) => {
            state.isLoading = true;
            state.isError = false;
            state.isSuccess = false;
            state.message = null;
        })
        .addCase(revokeKioskAccess.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.isError = false;
            const workerId = action.payload.workerId;
            state.kioskAccess = state.kioskAccess.filter(
                (w) => (w.id || w.workerId) !== workerId
            );
        })
        .addCase(revokeKioskAccess.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.isSuccess = false;
            state.message = action.payload;
        });
    }
})
export const { reset, setJobsiteId } = kioskManagementSlice.actions;
export default kioskManagementSlice.reducer